package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.AuthToken
import com.tixforgigs.apax.models.CartResponse
import com.tixforgigs.apax.models.Order
import com.tixforgigs.apax.models.OrderResponse
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.util.AuthUtil.currentBearerToken
import com.tixforgigs.common.core.util.AuthUtil.isUserLoggedIn
import com.tixforgigs.common.core.util.IdHelper.templateId
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mLatestCompletedOrderState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mShoppingCartState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUnresolvedOrderMetadataState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUnresolvedOrderState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUserLocaleState
import io.github.oshai.kotlinlogging.KotlinLogging
import io.github.oshai.kotlinlogging.logger
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.Json


object StorageUtil {

  val log by KotlinLogging.logger()

  sealed class StorageKey(val keyValue: String) {
    object ShoppingCart : StorageKey("shoppingCart-${templateId}")
    object SeatsHoldToken : StorageKey("seatsio-${templateId}")
    object TemplateEvents : StorageKey("templateEvents-${templateId}")

    /**** TODO making template specific if auth-flow <-> templateId as additional oauth header is fixed **/
    object UniqueId : StorageKey("uniqueId")
    object LoginStartLocation : StorageKey("loginStartLocation")
    object AuthToken : StorageKey("authToken")
    object AuthGrantType : StorageKey("authGrantType")

    /**** **/
    object AvailableShippingMethods : StorageKey("availableShippingMethods-${templateId}")
    object SelectedShippingMethod : StorageKey("selectedShippingMethod-${templateId}")
    object UserLocale : StorageKey("userLocale")
    object PaymentCountryCode : StorageKey("paymentCountry-${templateId}")
    object Orders : StorageKey("orders-${templateId}")
    object UnresolvedOrder : StorageKey("unresolvedOrder-${templateId}")
    object UnresolvedOrderMetadata : StorageKey("unresolvedOrderMetadata-${templateId}")
    object LatestCompletedOrder : StorageKey("latestCompletedOrder-$templateId")
  }

  object LocalStorageState {

    val mShoppingCartState = MutableStateFlow<CartResponse?>(getStorageItem(StorageKey.ShoppingCart))

    /** is currently not used, will replace mCurrentOrderState in future if orderItems are attached to OrderResponse */
    val mUnresolvedOrderMetadataState =
      MutableStateFlow<OrderResponse?>(getStorageItem(StorageKey.UnresolvedOrderMetadata))

    /** can be deleted if orderItems attached to OrderResponse */
    val mUnresolvedOrderState = MutableStateFlow<Order?>(getStorageItem(StorageKey.UnresolvedOrder))
    val mLatestCompletedOrderState = MutableStateFlow<Order?>(
      getStorageItem(
        key = StorageKey.LatestCompletedOrder,
        storageType = StorageType.SESSION_STORAGE
      )
    )
    val mUserLocaleState = MutableStateFlow<String?>(getStorageItem(StorageKey.UserLocale))
  }

  enum class StorageType {
    LOCAL_STORAGE,
    SESSION_STORAGE
  }

  inline fun <reified T> getStorageItem(
    key: StorageKey,
    storageType: StorageType = StorageType.LOCAL_STORAGE
  ): T? {
    return when (storageType) {
      StorageType.LOCAL_STORAGE -> window.localStorage.getItem(key.keyValue)
      StorageType.SESSION_STORAGE -> window.sessionStorage.getItem(key.keyValue)
    }?.let { json ->
      runCatching { Json.decodeFromString<T>(json) }.getOrNull()
    }.orElse { null }
  }

  inline fun <reified T> setStorageItem(
    key: StorageKey,
    item: T,
    serializer: KSerializer<T>,
    storageType: StorageType = StorageType.LOCAL_STORAGE
  ) {
    tryCatchSentry {
      Json.encodeToString(serializer, item)
    }.getOrNull()?.let { json ->
      when (storageType) {
        StorageType.LOCAL_STORAGE -> window.localStorage.setItem(key.keyValue, json)
        StorageType.SESSION_STORAGE -> window.sessionStorage.setItem(key.keyValue, json)
      }
      when (key) {
        StorageKey.ShoppingCart -> {
          mShoppingCartState.update { item as CartResponse? }
        }

        StorageKey.UnresolvedOrderMetadata -> {
          mUnresolvedOrderMetadataState.update { item as OrderResponse? }
        }

        StorageKey.UnresolvedOrder -> {
          mUnresolvedOrderState.update { item as Order? }
        }

        StorageKey.LatestCompletedOrder -> {
          mLatestCompletedOrderState.update { item as Order? }
        }

        StorageKey.UserLocale -> {
          mUserLocaleState.update { item as String? }
        }

        StorageKey.AuthToken -> {
          currentBearerToken.update { item.unsafeCast<AuthToken>() }
          isUserLoggedIn.update { true }
        }

        else -> Unit
      }
    }
  }

  fun deleteStorageItem(
    key: StorageKey,
    storageType: StorageType = StorageType.LOCAL_STORAGE
  ) {
    when (storageType) {
      StorageType.LOCAL_STORAGE -> window.localStorage.removeItem(key.keyValue)
      StorageType.SESSION_STORAGE -> window.sessionStorage.removeItem(key.keyValue)
    }
    when (key) {
      StorageKey.ShoppingCart -> {
        mShoppingCartState.update { null }
      }

      StorageKey.UnresolvedOrderMetadata -> {
        mUnresolvedOrderMetadataState.update { null }
      }

      StorageKey.UnresolvedOrder -> {
        mUnresolvedOrderState.update { null }
      }

      StorageKey.LatestCompletedOrder -> {
        mLatestCompletedOrderState.update { null }
      }

      StorageKey.UserLocale -> {
        mUserLocaleState.update { null }
      }

      StorageKey.AuthToken -> {
        currentBearerToken.update { null }
        isUserLoggedIn.update { false }
        deleteStorageItem(StorageKey.Orders)
      }

      else -> Unit
    }
  }
}
